import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { previewSchedule } from '../services/schedulePreview';
import type { SchedulePreview, SchedulePreviewParams } from '../types/schedule.types';

export const scheduleKeys = {
    all: ['schedule-preview'] as const,
    preview: (params: SchedulePreviewParams | null) => [...scheduleKeys.all, params] as const,
};

/**
 * Live elemis schedule preview, keyed by the exact preview request params
 * (D2/D4 of docs/plans/2026-07-14-payment-schedule-options.md). The query
 * key IS the race guard: React Query only ever serves the currently-active
 * key's data, so a late response for a superseded key is discarded
 * automatically — no manual request-cancellation bookkeeping is needed.
 * `placeholderData: keepPreviousData` keeps the previous schedule on screen
 * during a refetch instead of flashing back to empty/loading.
 *
 * `enabled` gates the call until there is a chosen method (non-null params)
 * and a positive amount, so no call fires for an incomplete selection.
 *
 * Debounce is the CALLER's responsibility (D4) — a discrete change (e.g.
 * picking a scheduling method) should be passed in immediately; a
 * continuous input (e.g. a future deposit field, Phase C) should be
 * debounced by the caller before it reaches `params`. This hook stays pure
 * and simply reacts to whatever `params` it is given.
 */
export const useSchedulePreview = (params: SchedulePreviewParams | null) => {
    return useQuery<SchedulePreview, Error>({
        queryKey: scheduleKeys.preview(params),
        queryFn: () => previewSchedule(params as SchedulePreviewParams),
        enabled: params !== null && params.amount > 0,
        placeholderData: keepPreviousData,
    });
};
